import React from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Database, Trash2, Mail, ChevronRight, Home, Smartphone } from "lucide-react";
import { Header, Footer } from "../components/Layout";

const PrivacyPolicyPage = () => {
  const lastUpdated = "26 de febrero de 2026";

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Política de Privacidad | Sin Adicciones Chile</title>
        <meta name="description" content="Política de privacidad de Sin Adicciones. Conoce cómo protegemos tus datos personales en nuestra plataforma web y aplicación móvil." />
        <link rel="canonical" href="https://sinadicciones.org/privacidad" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/privacidad" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <meta name="robots" content="noindex, follow" />
      

      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-emerald-400">Política de Privacidad</span>
          </nav>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Política de Privacidad</h1>
              <p className="text-slate-400 mt-1">Última actualización: {lastUpdated}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-lg prose-invert prose-emerald max-w-none">
          
          {/* Intro */}
          <div className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700 mb-8">
            <p className="text-slate-300 m-0">
              En <strong>Sin Adicciones</strong> nos comprometemos a proteger tu privacidad. Esta política explica qué datos recopilamos, 
              cómo los usamos y cuáles son tus derechos tanto en nuestra plataforma web (<strong>sinadicciones.org</strong>) como en 
              nuestra aplicación móvil (<strong>App SinAdicciones</strong>).
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">1. Información que Recopilamos</h2>
            </div>
            
            <h3 className="text-white text-lg font-semibold mt-6">1.1 Información que nos proporcionas</h3>
            <ul className="text-slate-300">
              <li><strong>Datos de registro:</strong> Nombre, correo electrónico, contraseña (encriptada), foto de perfil (opcional).</li>
              <li><strong>Perfil de usuario:</strong> Tipo de adicción (si decides compartirlo), fecha de inicio de recuperación, motivaciones personales.</li>
              <li><strong>Registros de hábitos:</strong> Hábitos saludables que deseas seguir y su cumplimiento diario.</li>
              <li><strong>Registros emocionales:</strong> Estado de ánimo, nivel de ansiedad, notas personales.</li>
              <li><strong>Comunicaciones:</strong> Mensajes enviados a través del chat TerapeutAI, consultas de soporte.</li>
            </ul>

            <h3 className="text-white text-lg font-semibold mt-6">1.2 Información recopilada automáticamente</h3>
            <ul className="text-slate-300">
              <li><strong>Datos de uso:</strong> Páginas visitadas, funciones utilizadas, tiempo en la aplicación.</li>
              <li><strong>Datos del dispositivo:</strong> Tipo de dispositivo, sistema operativo, identificador único (para notificaciones push).</li>
              <li><strong>Datos de ubicación:</strong> Solo si autorizas, para mostrarte centros cercanos. No almacenamos tu ubicación exacta.</li>
            </ul>

            <h3 className="text-white text-lg font-semibold mt-6">1.3 Información de terceros</h3>
            <ul className="text-slate-300">
              <li><strong>Inicio de sesión con Google:</strong> Si eliges registrarte con Google, recibimos tu nombre, email y foto de perfil de tu cuenta de Google.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">2. Cómo Usamos tu Información</h2>
            </div>
            
            <p className="text-slate-300">Utilizamos tus datos para:</p>
            <ul className="text-slate-300">
              <li><strong>Proporcionar nuestros servicios:</strong> Permitirte registrar tu progreso, seguir hábitos, y acceder a contenido educativo.</li>
              <li><strong>Personalizar tu experiencia:</strong> Mostrarte centros y profesionales relevantes según tu ubicación y necesidades.</li>
              <li><strong>Comunicarnos contigo:</strong> Enviarte recordatorios, notificaciones de logros, y actualizaciones importantes.</li>
              <li><strong>Mejorar nuestros servicios:</strong> Analizar patrones de uso (de forma agregada y anónima) para mejorar la plataforma.</li>
              <li><strong>Conectarte con profesionales:</strong> Si lo solicitas, compartir información relevante con terapeutas para tu tratamiento.</li>
              <li><strong>Cumplir obligaciones legales:</strong> Responder a requerimientos legales cuando sea necesario.</li>
            </ul>

            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl mt-6">
              <p className="text-emerald-300 m-0 text-sm">
                <strong>Importante:</strong> NUNCA vendemos tus datos personales a terceros. Tu información de salud es confidencial.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">3. Cómo Protegemos tu Información</h2>
            </div>
            
            <ul className="text-slate-300">
              <li><strong>Encriptación:</strong> Todas las comunicaciones usan HTTPS/TLS. Las contraseñas se almacenan con hash bcrypt.</li>
              <li><strong>Acceso restringido:</strong> Solo personal autorizado puede acceder a datos personales, y solo cuando es necesario.</li>
              <li><strong>Infraestructura segura:</strong> Utilizamos servidores en la nube con certificaciones de seguridad (MongoDB Atlas, servicios cloud).</li>
              <li><strong>Monitoreo:</strong> Sistemas de detección de accesos no autorizados y auditorías de seguridad periódicas.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">4. Permisos de la Aplicación Móvil</h2>
            </div>
            
            <p className="text-slate-300">La app SinAdicciones puede solicitar los siguientes permisos:</p>
            
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 text-emerald-400">Permiso</th>
                    <th className="text-left py-3 text-emerald-400">Propósito</th>
                    <th className="text-left py-3 text-emerald-400">¿Obligatorio?</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <tr className="border-b border-slate-800">
                    <td className="py-3">Notificaciones</td>
                    <td className="py-3">Recordatorios de hábitos y logros</td>
                    <td className="py-3">Opcional</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3">Ubicación</td>
                    <td className="py-3">Mostrar centros cercanos</td>
                    <td className="py-3">Opcional</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3">Cámara</td>
                    <td className="py-3">Foto de perfil</td>
                    <td className="py-3">Opcional</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3">Almacenamiento</td>
                    <td className="py-3">Guardar datos offline</td>
                    <td className="py-3">Opcional</td>
                  </tr>
                  <tr>
                    <td className="py-3">Internet</td>
                    <td className="py-3">Sincronizar datos</td>
                    <td className="py-3">Requerido</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p className="text-slate-400 text-sm mt-4">
              Puedes revocar cualquier permiso opcional en la configuración de tu dispositivo.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">5. Tus Derechos</h2>
            </div>
            
            <p className="text-slate-300">De acuerdo con la Ley 19.628 sobre Protección de Datos Personales de Chile, tienes derecho a:</p>
            <ul className="text-slate-300">
              <li><strong>Acceso:</strong> Solicitar una copia de tus datos personales.</li>
              <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos.</li>
              <li><strong>Eliminación:</strong> Solicitar la eliminación de tu cuenta y datos asociados.</li>
              <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos para ciertos fines.</li>
              <li><strong>Portabilidad:</strong> Recibir tus datos en un formato estructurado.</li>
            </ul>
            
            <p className="text-slate-300 mt-4">
              Para ejercer estos derechos, contáctanos a <a href="mailto:contacto@sinadicciones.cl" className="text-emerald-400 hover:underline">contacto@sinadicciones.cl</a>
            </p>

            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl mt-6">
              <h4 className="text-white font-semibold mb-2">Eliminar tu cuenta</h4>
              <p className="text-slate-400 text-sm m-0">
                Puedes eliminar tu cuenta desde la configuración de la app o enviándonos un correo. 
                Al eliminar tu cuenta, se borrarán permanentemente todos tus datos personales, registros de hábitos, 
                y progreso de recuperación. Esta acción no se puede deshacer.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">6. Retención de Datos</h2>
            <ul className="text-slate-300">
              <li><strong>Cuenta activa:</strong> Mantenemos tus datos mientras tu cuenta esté activa.</li>
              <li><strong>Cuenta inactiva:</strong> Después de 2 años de inactividad, podemos contactarte antes de eliminar tu cuenta.</li>
              <li><strong>Después de eliminación:</strong> Los datos se eliminan dentro de 30 días, excepto cuando la ley requiera retención.</li>
              <li><strong>Datos anónimos:</strong> Podemos retener datos agregados y anónimos indefinidamente para análisis estadístico.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">7. Servicios de Terceros</h2>
            <p className="text-slate-300">Utilizamos los siguientes servicios de terceros:</p>
            <ul className="text-slate-300">
              <li><strong>MongoDB Atlas:</strong> Almacenamiento seguro de datos en la nube.</li>
              <li><strong>Google Authentication:</strong> Inicio de sesión con Google (opcional).</li>
              <li><strong>OpenAI:</strong> Para el chatbot TerapeutAI (los mensajes no se usan para entrenar modelos).</li>
              <li><strong>Resend:</strong> Envío de correos electrónicos transaccionales.</li>
              <li><strong>Expo Push Notifications:</strong> Notificaciones en la app móvil.</li>
            </ul>
            <p className="text-slate-400 text-sm mt-4">
              Cada servicio tiene su propia política de privacidad. Te recomendamos revisarlas.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">8. Menores de Edad</h2>
            <p className="text-slate-300">
              Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos intencionalmente 
              información de menores de edad. Si eres padre/madre y descubres que tu hijo menor ha proporcionado 
              datos personales, contáctanos para eliminarlos.
            </p>
            <p className="text-slate-300 mt-4">
              Para adolescentes entre 14-17 años que necesiten ayuda con adicciones, recomendamos que un 
              adulto responsable supervise el uso de la plataforma.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white">9. Cambios a esta Política</h2>
            <p className="text-slate-300">
              Podemos actualizar esta política ocasionalmente. Te notificaremos sobre cambios significativos 
              mediante un aviso en la app o por correo electrónico. Te recomendamos revisar esta página periódicamente.
            </p>
            <p className="text-slate-300 mt-4">
              La fecha de "última actualización" al inicio de esta página indica cuándo se realizaron los últimos cambios.
            </p>
          </section>

          {/* Section 10 - Contact */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <h2 className="text-2xl font-bold text-white m-0">10. Contacto</h2>
            </div>
            
            <p className="text-slate-300">Si tienes preguntas sobre esta política de privacidad, puedes contactarnos:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <h4 className="text-white font-semibold mb-2">Correo electrónico</h4>
                <a href="mailto:contacto@sinadicciones.cl" className="text-emerald-400 hover:underline">
                  contacto@sinadicciones.cl
                </a>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <h4 className="text-white font-semibold mb-2">Sitio web</h4>
                <a href="https://sinadicciones.org" className="text-emerald-400 hover:underline">
                  sinadicciones.org
                </a>
              </div>
            </div>
          </section>

        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-r from-emerald-900/30 to-slate-800/50 rounded-2xl border border-emerald-500/20 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            ¿Tienes dudas sobre tus datos?
          </h3>
          <p className="text-slate-300 mb-6">
            Estamos comprometidos con la transparencia. No dudes en contactarnos si tienes preguntas.
          </p>
          <a 
            href="mailto:contacto@sinadicciones.cl"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors"
          >
            <Mail className="w-5 h-5" />
            Contactar
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
